import React, { useContext, useEffect, useState } from 'react'
import { Alert, AlertTitle, Typography } from '@mui/material'

//constants
import { PAYOUT_MAINTENANCE } from 'constants/index'
//contexts
import { DeltaTimeContext } from 'context/DeltaTimeContext'
//utils
import { AlertNoteIcon } from 'utils/icons'
import {
  getMsUntilPayoutMaintenanceChange,
  isPayoutMaintenanceGiftCard,
  shouldShowPayoutMaintenanceNotice,
} from 'utils/payoutMaintenance'

interface PayoutMaintenanceNoticeProps {
  giftCardName?: string
  style?: object
}

// setTimeout overflows above 2^31 - 1 ms (~24.8 days)
const MAX_TIMEOUT_IN_MILLIS = 2 ** 31 - 1

const PayoutMaintenanceNotice = ({ giftCardName, style }: PayoutMaintenanceNoticeProps) => {
  const { delta } = useContext(DeltaTimeContext)
  const [boundaryTick, setBoundaryTick] = useState(0)

  const isMaintenanceGiftCard = isPayoutMaintenanceGiftCard(giftCardName)

  // Re-render when the window starts or ends so an open confirmation stays accurate
  useEffect(() => {
    if (!isMaintenanceGiftCard) {
      return
    }

    const msUntilChange = getMsUntilPayoutMaintenanceChange(Date.now() + (delta ?? 0))
    if (msUntilChange === null) {
      return
    }

    const timer = setTimeout(
      () => setBoundaryTick((tick) => tick + 1),
      Math.min(msUntilChange, MAX_TIMEOUT_IN_MILLIS)
    )
    return () => clearTimeout(timer)
  }, [isMaintenanceGiftCard, delta, boundaryTick])

  if (!shouldShowPayoutMaintenanceNotice(giftCardName, Date.now() + (delta ?? 0))) {
    return null
  }

  return (
    <Alert severity="info" icon={false} sx={{ ...styles.alert, ...style }}>
      <AlertTitle>
        <AlertNoteIcon />
        <Typography variant="h2" sx={styles.alertTitle}>
          Payout delay
        </Typography>
      </AlertTitle>
      <Typography variant="body2" sx={styles.alertText}>
        {PAYOUT_MAINTENANCE.TEXT}
      </Typography>
    </Alert>
  )
}

const styles = {
  alert: {
    textAlign: 'left',
    background: '#FFF7ED',
    borderRadius: '16px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 500,
    padding: '8px 12px',
    '& .MuiAlertTitle-root': {
      display: 'flex',
      gap: '8px',
      marginTop: 0,
      marginBottom: '10px',
    },
    '& .MuiAlert-message': {
      padding: 0,
    },
  },
  alertTitle: {
    fontWeight: 600,
    color: '#3F3F46',
    margin: 'auto 0',
  },
  alertText: {
    fontWeight: 500,
    color: '#3F3F46',
  },
}

export default PayoutMaintenanceNotice
